/**
 * Onboarding Phase Tracker Component
 * Shows progress across all 6 onboarding phases
 */
import React from 'react';
import { Check, Circle, Lock, CheckCircle2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const PHASES = [
  { id: 1, name: 'Business Context', description: 'Company overview & terminology' },
  { id: 2, name: 'Server Architecture', description: 'Server purposes & safety rules' },
  { id: 3, name: 'Table Definitions', description: 'What each table does' },
  { id: 4, name: 'Relationships', description: 'How tables connect' },
  { id: 5, name: 'Query Rules', description: 'Indexes & performance' },
  { id: 6, name: 'AI Deep Learning', description: 'Targeted AI questions' }
];

const PhaseTracker = ({ currentPhase, phaseProgress = {}, onPhaseClick, user, onMarkComplete }) => {
  // Normalize progress keys (JSON converts number keys to strings)
  const normalizedProgress = {};
  Object.keys(phaseProgress).forEach(key => {
    normalizedProgress[parseInt(key)] = phaseProgress[key];
  });
  
  const handleMarkComplete = async (phaseId) => {
    if (onMarkComplete) {
      await onMarkComplete(phaseId);
    }
  };
  
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-800">AI Onboarding Progress</h2>
        
        {/* Admin: Mark current phase complete button */}
        {user?.role === 'admin' && currentPhase >= 1 && currentPhase <= 6 && normalizedProgress[currentPhase] < 100 && normalizedProgress[currentPhase] >= 50 && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleMarkComplete(currentPhase)}
            className="text-green-600 hover:text-green-700 hover:bg-green-50 border-green-300"
            title={`Mark Phase ${currentPhase} as complete (admin override)`}
          >
            <CheckCircle2 className="w-4 h-4 mr-1" />
            Mark Phase {currentPhase} Complete
          </Button>
        )}
      </div>
      
      <div className="flex items-center justify-between mb-4">
        {PHASES.map((phase, index) => {
          const progress = normalizedProgress[phase.id] || 0;
          const isComplete = progress >= 100;
          const isCurrent = currentPhase === phase.id;
          // Allow access to: current phase, previous phases, next phase, or any complete phase
          const previousPhaseComplete = phase.id === 1 || (normalizedProgress[phase.id - 1] || 0) >= 100;
          const isAccessible = phase.id <= currentPhase || phase.id === currentPhase + 1 || isComplete || previousPhaseComplete;
          const isLocked = !isAccessible;
          
          return (
            <React.Fragment key={phase.id}>
              {/* Phase Circle */}
              <button
                onClick={() => !isLocked && onPhaseClick(phase.id)}
                disabled={isLocked}
                className={cn(
                  "flex flex-col items-center group transition-all",
                  isLocked ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
                )}
              >
                <div className={cn(
                  "w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all",
                  isComplete ? "bg-green-500 border-green-500 text-white" :
                  isCurrent ? "bg-amber-500 border-amber-500 text-white" :
                  isLocked ? "bg-gray-100 border-gray-300 text-gray-400" :
                  "bg-white border-gray-300 text-gray-500 group-hover:border-amber-400"
                )}>
                  {isComplete ? (
                    <Check className="w-5 h-5" />
                  ) : isLocked ? (
                    <Lock className="w-4 h-4" />
                  ) : (
                    <span className="text-sm font-medium">{phase.id}</span>
                  )}
                </div>
                <span className={cn(
                  "text-xs mt-2 font-medium text-center max-w-[80px]",
                  isCurrent ? "text-amber-600" : isComplete ? "text-green-600" : "text-gray-500"
                )}>
                  {phase.name}
                </span>
                {!isLocked && progress > 0 && progress < 100 && (
                  <span className="text-[10px] text-gray-400">{progress}%</span>
                )}
              </button>
              
              {/* Connector Line */}
              {index < PHASES.length - 1 && (
                <div className="flex-1 mx-2 h-0.5 bg-gray-200 relative">
                  <div 
                    className="absolute inset-y-0 left-0 bg-green-500 transition-all duration-500"
                    style={{ width: `${isComplete ? 100 : 0}%` }}
                  />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
      
      {/* Current Phase Description */}
      <div className="text-center text-sm text-gray-500">
        {PHASES.find(p => p.id === currentPhase)?.description}
      </div>
    </div>
  );
};

export { PhaseTracker, PHASES };
